/**
 * 长期记忆仓储（T13）：CRUD + 标签筛选 + 按重要度/更新时间排序 + 分页。
 *
 * 表结构见 `src/core/db.ts` 的 `memories`（索引 id / updatedAt / createdAt / importance / *tags）。
 * 排序键（重要度或更新时间）与 id 组成稳定全序，游标分页据此过滤「排在游标之后」的记录，
 * 保证翻页不重不漏；同分时更新时间新的优先。
 */

import type { Table } from 'dexie';

import { getDb } from '../db';
import { newId } from '../ids';
import type { MemoryEntry, MemoryImportance } from '../types';
import { normalizeLimit, type Page, type PageQuery } from './pagination';

export type MemorySortField = 'importance' | 'updatedAt';
export type MemorySortOrder = 'asc' | 'desc';

export interface CreateMemoryInput {
  id?: string;
  content: string;
  tags?: string[];
  importance?: number;
  createdAt?: number;
  updatedAt?: number;
  sourceConversationId?: string;
  lastRecalledAt?: number;
  recallCount?: number;
}

export interface MemoryQuery extends PageQuery {
  /** 按标签精确筛选。 */
  tag?: string;
  /** 排序字段，默认按更新时间。 */
  sortBy?: MemorySortField;
  /** 排序方向，默认 desc（新的 / 重要的在前）。 */
  order?: MemorySortOrder;
}

/** 重要度收敛到 1..5 的整数，非法值回落到默认 3。 */
export function normalizeImportance(value: number | undefined): MemoryImportance {
  if (value === undefined || !Number.isFinite(value)) return 3;
  const clamped = Math.min(5, Math.max(1, Math.round(value)));
  return clamped as MemoryImportance;
}

/** 标签去重 + 去空白 + 去空串；保留原始大小写与顺序。 */
export function normalizeTags(tags: readonly string[] | undefined): string[] {
  if (tags === undefined) return [];
  const seen = new Set<string>();
  const result: string[] = [];
  for (const raw of tags) {
    const tag = raw.trim();
    if (tag === '' || seen.has(tag)) continue;
    seen.add(tag);
    result.push(tag);
  }
  return result;
}

function table(): Table<MemoryEntry, string> {
  return getDb().memories;
}

/** 纯构造：只归一化字段，不落库（便于单测与调用方复用）。 */
export function createMemory(input: CreateMemoryInput): MemoryEntry {
  const createdAt = input.createdAt ?? Date.now();
  return {
    id: input.id ?? newId(),
    content: input.content,
    tags: normalizeTags(input.tags),
    importance: normalizeImportance(input.importance),
    createdAt,
    updatedAt: input.updatedAt ?? createdAt,
    recallCount: input.recallCount ?? 0,
    ...(input.sourceConversationId !== undefined
      ? { sourceConversationId: input.sourceConversationId }
      : {}),
    ...(input.lastRecalledAt !== undefined ? { lastRecalledAt: input.lastRecalledAt } : {}),
  };
}

export async function putMemory(memory: MemoryEntry): Promise<string> {
  return table().put(memory);
}

export async function createAndSaveMemory(input: CreateMemoryInput): Promise<MemoryEntry> {
  const memory = createMemory(input);
  await putMemory(memory);
  return memory;
}

export async function getMemory(id: string): Promise<MemoryEntry | undefined> {
  return table().get(id);
}

/** 比较器：负数表示 a 排在 b 之前；同分时更新时间新的优先，最后用 id 兜底。 */
export function compareMemories(
  a: MemoryEntry,
  b: MemoryEntry,
  sortBy: MemorySortField,
  order: MemorySortOrder,
): number {
  const direction = order === 'asc' ? 1 : -1;
  const primaryA = sortBy === 'importance' ? a.importance : a.updatedAt;
  const primaryB = sortBy === 'importance' ? b.importance : b.updatedAt;
  if (primaryA !== primaryB) return (primaryA - primaryB) * direction;
  if (a.updatedAt !== b.updatedAt) return b.updatedAt - a.updatedAt;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

function resolveSort(query: MemoryQuery): { sortBy: MemorySortField; order: MemorySortOrder } {
  return { sortBy: query.sortBy ?? 'updatedAt', order: query.order ?? 'desc' };
}

/** 列出（可按标签筛选），并已按查询的排序规则排好序。 */
export async function listMemories(query: MemoryQuery = {}): Promise<MemoryEntry[]> {
  const { sortBy, order } = resolveSort(query);
  const all = await table().toArray();
  const tag = query.tag;
  const filtered = tag === undefined ? all : all.filter((memory) => memory.tags.includes(tag));
  return filtered.sort((a, b) => compareMemories(a, b, sortBy, order));
}

function encodeMemoryCursor(memory: MemoryEntry): string {
  return `${memory.importance}:${memory.updatedAt}:${memory.id}`;
}

function decodeMemoryCursor(
  cursor: string,
): { importance: MemoryImportance; updatedAt: number; id: string } | null {
  const parts = cursor.split(':');
  if (parts.length < 3) return null;
  const [importanceRaw, updatedAtRaw, ...idParts] = parts;
  const id = idParts.join(':');
  if (importanceRaw === undefined || updatedAtRaw === undefined || id === '') return null;
  const importance = Number(importanceRaw);
  const updatedAt = Number(updatedAtRaw);
  if (!Number.isFinite(importance) || !Number.isFinite(updatedAt)) return null;
  return { importance: normalizeImportance(importance), updatedAt, id };
}

/**
 * 游标分页：排序规则与 `listMemories` 一致。
 * 游标记录上一页最后一条的完整排序键，下一页只取严格排在它之后的记录。
 */
export async function listMemoriesPage(query: MemoryQuery = {}): Promise<Page<MemoryEntry>> {
  const limit = normalizeLimit(query.limit);
  const { sortBy, order } = resolveSort(query);
  const all = await listMemories(query);
  const cursor = query.cursor ? decodeMemoryCursor(query.cursor) : null;
  const ordered =
    cursor === null
      ? all
      : all.filter((memory) => {
          const pivot: MemoryEntry = {
            id: cursor.id,
            content: '',
            tags: [],
            importance: cursor.importance,
            createdAt: cursor.updatedAt,
            updatedAt: cursor.updatedAt,
            recallCount: 0,
          };
          return compareMemories(memory, pivot, sortBy, order) > 0;
        });

  const items = ordered.slice(0, limit);
  const hasMore = ordered.length > limit;
  const last = items[items.length - 1];
  return {
    items,
    hasMore,
    nextCursor: hasMore && last ? encodeMemoryCursor(last) : null,
  };
}

/** 更新：保留 id / createdAt，默认把 updatedAt 刷新为当前时间。 */
export async function updateMemory(
  id: string,
  patch: Partial<Omit<MemoryEntry, 'id' | 'createdAt'>>,
): Promise<MemoryEntry | undefined> {
  const existing = await getMemory(id);
  if (!existing) return undefined;
  const next: MemoryEntry = {
    ...existing,
    ...patch,
    id,
    createdAt: existing.createdAt,
    updatedAt: patch.updatedAt ?? Date.now(),
    tags: patch.tags === undefined ? existing.tags : normalizeTags(patch.tags),
    importance:
      patch.importance === undefined ? existing.importance : normalizeImportance(patch.importance),
    recallCount: patch.recallCount ?? existing.recallCount,
  };
  await putMemory(next);
  return next;
}

/** 记录一次召回：累加 recallCount 并刷新 lastRecalledAt（用于召回策略回写）。 */
export async function markRecalled(ids: readonly string[], at: number = Date.now()): Promise<void> {
  if (ids.length === 0) return;
  const db = getDb();
  await db.transaction('rw', db.memories, async () => {
    for (const id of ids) {
      const existing = await db.memories.get(id);
      if (existing === undefined) continue;
      await db.memories.put({
        ...existing,
        recallCount: existing.recallCount + 1,
        lastRecalledAt: at,
      });
    }
  });
}

export async function deleteMemory(id: string): Promise<void> {
  await table().delete(id);
}

export async function countMemories(): Promise<number> {
  return table().count();
}

export async function clearMemories(): Promise<void> {
  await table().clear();
}